import { useEffect, useMemo, useState } from 'react';
import Map, { MapMarker } from '../Map';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ComboboxSingle } from '../ui/combobox';
import { Machine } from '../../types/map';
import { getMachines } from '../../lib/api';
import { Checkbox } from '../ui/checkbox';
import { Filter, List as ListIcon, Map as MapIcon, ChevronDown, X, Calendar, Clock } from 'lucide-react';
import { MAP_STYLES, MapStyle, DEFAULT_MAP_STYLE } from '../../lib/mapConfig';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import BeforeAfterSlider from '../BeforeAfterSlider';

const DEFAULT_MAP_CENTER: [number, number] = [54.0, -2.5];
const DEFAULT_MAP_ZOOM = 5.8;

const venueTypeOptions = [
  { value: 'all', label: 'All venue types' },
  { value: 'Health & Fitness', label: 'Health & Fitness' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Universities', label: 'Universities' },
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'Live/Available', label: 'Live/Available' },
  { value: 'Reserved', label: 'Reserved' },
  { value: 'Coming Soon', label: 'Coming Soon' },
];

interface MachineSelectionStepProps {
  selectedMachines: Machine[];
  onSelectionChange: (machines: Machine[]) => void;
  onSkipToRetailMedia: () => void;
}

export function MachineSelectionStep({
  selectedMachines,
  onSelectionChange,
  onSkipToRetailMedia,
}: MachineSelectionStepProps) {
  const [allMachines, setAllMachines] = useState<Machine[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [venueTypeFilter, setVenueTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [availabilityTypeFilter, setAvailabilityTypeFilter] = useState<string>('all');
  const [hasAvailableSkus, setHasAvailableSkus] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [mapStyle] = useState<MapStyle>(DEFAULT_MAP_STYLE);
  const [expandedVenueTypes, setExpandedVenueTypes] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [availabilityTypeOptions, setAvailabilityTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: 'all', label: 'All availability types' },
  ]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedMachineForDetails, setSelectedMachineForDetails] = useState<Machine | null>(null);

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      
      // Load availability type options
      try {
        const availTypes = await fetch('/api/admin/field-options?fieldName=availabilityType', {
          credentials: 'include',
        });
        if (availTypes.ok) {
          const data = await availTypes.json();
          const options = [
            { value: 'all', label: 'All availability types' },
            ...data.map((opt: any) => ({ value: opt.value, label: opt.value })),
          ];
          setAvailabilityTypeOptions(options);
        }
      } catch (error) {
        console.error('Error loading availability types:', error);
      }
      
      const machines = await getMachines();
      setAllMachines(machines);
    } catch (error) {
      console.error('Failed to load machines:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMachines = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let result = allMachines;

    if (term) {
      result = result.filter((machine) => {
        return (
          machine.name.toLowerCase().includes(term) ||
          (machine.description && machine.description.toLowerCase().includes(term)) ||
          (machine.label && machine.label.toLowerCase().includes(term)) ||
          (machine.venueName && machine.venueName.toLowerCase().includes(term)) ||
          (machine.location && machine.location.toLowerCase().includes(term))
        );
      });
    }

    if (venueTypeFilter !== 'all') {
      result = result.filter((machine) => machine.venueType === venueTypeFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter((machine) => machine.status === statusFilter);
    }

    if (availabilityTypeFilter !== 'all') {
      result = result.filter((machine) => machine.availabilityType === availabilityTypeFilter);
    }

    if (hasAvailableSkus === 'yes') {
      result = result.filter((machine) => machine.availableSkus && machine.availableSkus > 0);
    } else if (hasAvailableSkus === 'no') {
      result = result.filter((machine) => !machine.availableSkus || machine.availableSkus === 0);
    }

    return result.sort((a, b) => {
      const aActive = a.isActive ? 1 : 0;
      const bActive = b.isActive ? 1 : 0;
      if (bActive !== aActive) {
        return bActive - aActive;
      }
      return a.name.localeCompare(b.name);
    });
  }, [allMachines, searchTerm, venueTypeFilter, statusFilter, availabilityTypeFilter, hasAvailableSkus]);

  const groupedMachines = useMemo(() => {
    const groups = new globalThis.Map<string, globalThis.Map<string, globalThis.Map<string, Machine[]>>>();
    
    filteredMachines.forEach((machine) => {
      const venueType = machine.venueType || 'Uncategorized';
      const status = machine.status || 'Live/Available';
      const venueName = machine.venueName || 'Other';
      
      if (!groups.has(venueType)) {
        groups.set(venueType, new globalThis.Map());
      }
      const venueMap = groups.get(venueType)!;
      
      if (!venueMap.has(status)) {
        venueMap.set(status, new globalThis.Map());
      }
      const statusMap = venueMap.get(status)!;
      
      if (!statusMap.has(venueName)) {
        statusMap.set(venueName, []);
      }
      statusMap.get(venueName)!.push(machine);
    });
    
    return groups;
  }, [filteredMachines]);

  // Expand venue types on initial load
  useEffect(() => {
    if (groupedMachines.size > 0 && expandedVenueTypes.size === 0) {
      const venueTypes = Array.from(groupedMachines.keys());
      setExpandedVenueTypes(new Set(venueTypes));
    }
  }, [groupedMachines]);

  const visibleMachines = useMemo(() => {
    if (!mapBounds) return filteredMachines;
    
    return filteredMachines.filter((machine) => {
      return (
        machine.lat >= mapBounds.south &&
        machine.lat <= mapBounds.north &&
        machine.lng >= mapBounds.west &&
        machine.lng <= mapBounds.east
      );
    });
  }, [filteredMachines, mapBounds]);

  const mapMarkers: MapMarker[] = useMemo(() => {
    const selectedIds = new Set(selectedMachines.map(m => m.id));
    return filteredMachines.map((machine) => {
      const isSelected = selectedIds.has(machine.id);
      return {
        lat: machine.lat,
        lng: machine.lng,
        title: machine.label ? `${machine.name} (${machine.label})` : machine.name,
        color: machine.pinColor || undefined,
        onClick: () => {
          setSelectedMachineForDetails(machine);
        },
      };
    });
  }, [filteredMachines, selectedMachines]);

  const handleToggleSelection = (machine: Machine) => {
    const isSelected = selectedMachines.some(m => m.id === machine.id);
    if (isSelected) {
      onSelectionChange(selectedMachines.filter(m => m.id !== machine.id));
    } else {
      onSelectionChange([...selectedMachines, machine]);
    }
  };

  const handleSelectAll = (machines: Machine[]) => {
    const allSelected = machines.every(m => selectedMachines.some(sm => sm.id === m.id));
    if (allSelected) {
      onSelectionChange(selectedMachines.filter(sm => !machines.some(m => m.id === sm.id)));
    } else {
      const newSelections = machines.filter(m => !selectedMachines.some(sm => sm.id === m.id));
      onSelectionChange([...selectedMachines, ...newSelections]);
    }
  };

  const toggleVenueType = (venueType: string) => {
    setExpandedVenueTypes(prev => {
      const next = new Set(prev);
      if (next.has(venueType)) {
        next.delete(venueType);
      } else {
        next.add(venueType);
      }
      return next;
    });
  };

  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderStatusPill = (status: string) => {
    let label = status;
    let colorClasses = '';
    let showDot = false;

    if (status === 'Live/Available') {
      label = 'Live';
      colorClasses = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      showDot = true;
    } else if (status === 'Reserved') {
      label = 'Reserved';
      colorClasses = 'bg-amber-100 text-amber-700 border border-amber-200';
    } else if (status === 'Coming Soon') {
      label = 'Coming Soon';
      colorClasses = 'bg-blue-100 text-blue-700 border border-blue-200';
    } else {
      colorClasses = 'bg-slate-100 text-slate-700 border border-slate-200';
    }

    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${colorClasses}`}>
        {showDot && <span className="w-2 h-2 rounded-full bg-emerald-600"></span>}
        {label}
      </span>
    );
  };

  // Helper function to count selected machines in a list
  const countSelectedInMachines = (machines: Machine[]) => {
    const selectedIds = new Set(selectedMachines.map(m => m.id));
    return machines.filter(m => selectedIds.has(m.id)).length;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Select Machines</h2>
        <p className="text-slate-600">
          Choose the machines you want to include in your campaign. You've selected {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''}.
        </p>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-end">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              viewMode === 'list'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setViewMode('list')}
          >
            <ListIcon className="h-4 w-4" />
            <span>List</span>
          </button>
          <button
            type="button"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              viewMode === 'map'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setViewMode('map')}
          >
            <MapIcon className="h-4 w-4" />
            <span>Map</span>
          </button>
        </div>
      </div>

      {viewMode === 'list' && (
        <>
          {/* Filters */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </Button>
          </div>

          {filtersExpanded && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-slate-600" />
                <h2 className="text-xl font-semibold text-slate-900">Filters</h2>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full"
                />
                
                <ComboboxSingle
                  options={venueTypeOptions}
                  value={venueTypeFilter}
                  onValueChange={setVenueTypeFilter}
                  placeholder="All venue types"
                  searchPlaceholder="Search venue types..."
                  emptyMessage="No venue types found."
                />

                <ComboboxSingle
                  options={statusOptions}
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                  placeholder="All statuses"
                  searchPlaceholder="Search statuses..."
                  emptyMessage="No statuses found."
                />

                <ComboboxSingle
                  options={availabilityTypeOptions}
                  value={availabilityTypeFilter}
                  onValueChange={setAvailabilityTypeFilter}
                  placeholder="All availability types"
                  searchPlaceholder="Search availability types..."
                  emptyMessage="No availability types found."
                />
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">Has Available SKUs:</span>
                <ComboboxSingle
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ]}
                  value={hasAvailableSkus}
                  onValueChange={setHasAvailableSkus}
                  placeholder="All"
                  className="w-[120px]"
                />
              </div>
            </div>
          )}

          {/* Machine List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="text-slate-500">Loading machines...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {Array.from(groupedMachines.entries()).map(([venueType, statusMap]) => {
                if (!statusMap || statusMap.size === 0) return null;

                let totalMachines = 0;
                let totalAvailable = 0;
                let totalSelected = 0;
                statusMap.forEach((subcategoriesMap) => {
                  subcategoriesMap.forEach((machines) => {
                    totalMachines += machines.length;
                    totalAvailable += machines.filter(m => m.isActive).length;
                    totalSelected += countSelectedInMachines(machines);
                  });
                });

                const isVenueTypeExpanded = expandedVenueTypes.has(venueType);

                return (
                  <div key={venueType} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    {/* Venue Type Header */}
                    <div className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <button
                        onClick={() => toggleVenueType(venueType)}
                        className="flex items-center gap-3 flex-1"
                      >
                        <ChevronDown 
                          className={`h-5 w-5 text-slate-400 transition-transform ${isVenueTypeExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                        />
                        <div className="text-left flex items-center gap-2">
                          <div>
                            <h2 className="text-lg font-semibold text-slate-900">{venueType}</h2>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                              {totalMachines} {totalMachines === 1 ? 'Machine' : 'Machines'} • {totalAvailable} Available
                            </p>
                          </div>
                          {totalSelected > 0 && (
                            <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold px-2 py-1 min-h-[24px]">
                              {totalSelected}
                            </span>
                          )}
                        </div>
                      </button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const allMachines: Machine[] = [];
                          statusMap.forEach((subcategoriesMap) => {
                            subcategoriesMap.forEach((machines) => {
                              allMachines.push(...machines);
                            });
                          });
                          handleSelectAll(allMachines);
                        }}
                        className="text-xs"
                      >
                        {totalSelected > 0 && totalSelected === totalMachines ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>

                    {/* Status Sections */}
                    {isVenueTypeExpanded && (
                      <div className="border-t border-slate-100">
                        {Array.from(statusMap.entries()).map(([status, subcategoriesMap]) => {
                          let statusTotalMachines = 0;
                          let statusTotalAvailable = 0;
                          let statusTotalSelected = 0;
                          subcategoriesMap.forEach((machines) => {
                            statusTotalMachines += machines.length;
                            statusTotalAvailable += machines.filter(m => m.isActive).length;
                            statusTotalSelected += countSelectedInMachines(machines);
                          });

                          const statusKey = `${venueType}:${status}`;
                          const isStatusExpanded = expandedSubcategories.has(statusKey);

                          return (
                            <div key={statusKey} className="border-b border-slate-100 last:border-b-0">
                              <div className="p-3 pl-8 flex items-center justify-between hover:bg-slate-50 transition-colors bg-blue-50/30">
                                <button
                                  onClick={() => toggleSubcategory(statusKey)}
                                  className="flex items-center gap-2 flex-1"
                                >
                                  <ChevronDown 
                                    className={`h-4 w-4 text-slate-400 transition-transform ${isStatusExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                                  />
                                  <div className="text-left flex items-center gap-2">
                                    {renderStatusPill(status)}
                                    <div className="flex items-center gap-2">
                                      <div>
                                        <h3 className="text-sm font-semibold text-slate-700">{status}</h3>
                                        <p className="text-xs text-slate-500">
                                          {statusTotalMachines} {statusTotalMachines === 1 ? 'Machine' : 'Machines'} • {statusTotalAvailable} Available
                                        </p>
                                      </div>
                                      {statusTotalSelected > 0 && (
                                        <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold px-2 py-0.5 min-h-[20px]">
                                          {statusTotalSelected}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const statusMachines: Machine[] = [];
                                    subcategoriesMap.forEach((machines) => {
                                      statusMachines.push(...machines);
                                    });
                                    handleSelectAll(statusMachines);
                                  }}
                                  className="text-xs mr-2"
                                >
                                  {statusTotalSelected > 0 && statusTotalSelected === statusTotalMachines ? 'Deselect All' : 'Select All'}
                                </Button>
                              </div>

                              {/* Venue Names */}
                              {isStatusExpanded && (
                                <div className="border-t border-slate-100 bg-white">
                                  {Array.from(subcategoriesMap.entries()).map(([venueName, machines]) => {
                                    const subcategoryKey = `${venueType}:${status}:${venueName}`;
                                    const isSubcategoryExpanded = expandedSubcategories.has(subcategoryKey);
                                    const availableCount = machines.filter(m => m.isActive).length;
                                    const selectedCount = countSelectedInMachines(machines);

                                    return (
                                      <div key={subcategoryKey} className="border-b border-slate-100 last:border-b-0">
                                        <div className="p-3 pl-16 flex items-center justify-between hover:bg-slate-50 transition-colors bg-slate-50/50">
                                          <button
                                            onClick={() => toggleSubcategory(subcategoryKey)}
                                            className="flex items-center gap-2 flex-1"
                                          >
                                            <ChevronDown 
                                              className={`h-4 w-4 text-slate-400 transition-transform ${isSubcategoryExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                                            />
                                            <div className="text-left flex items-center gap-2">
                                              <div>
                                                <h3 className="text-sm font-semibold text-slate-700">{venueName}</h3>
                                                <p className="text-xs text-slate-500">
                                                  {machines.length} {machines.length === 1 ? 'Machine' : 'Machines'} • {availableCount} Available
                                                </p>
                                              </div>
                                              {selectedCount > 0 && (
                                                <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold px-2 py-0.5 min-h-[20px]">
                                                  {selectedCount}
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSelectAll(machines);
                                            }}
                                            className="text-xs mr-2"
                                          >
                                            {selectedCount > 0 && selectedCount === machines.length ? 'Deselect All' : 'Select All'}
                                          </Button>
                                        </div>

                                        {/* Machine List */}
                                        {isSubcategoryExpanded && (
                                          <div className="divide-y divide-slate-100 bg-white">
                                            {machines.map((machine) => {
                                              const isSelected = selectedMachines.some(m => m.id === machine.id);
                                              const displayStatus: string = machine.status || 'Live/Available';
                                              const venueType = machine.venueType || '—';
                                              const location = machine.location || '—';
                                              const availabilityType = machine.availabilityType || '—';
                                              const venueLabel = machine.venueName || machine.venueType || 'Venue';

                                              return (
                                                <div
                                                  key={machine.id}
                                                  onClick={() => handleToggleSelection(machine)}
                                                  className={`relative flex flex-col gap-4 p-4 transition-colors md:flex-row md:items-center md:justify-between cursor-pointer ${
                                                    isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                                                  }`}
                                                >
                                                  {/* Status Badge - Top Right Corner */}
                                                  <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                                                      displayStatus === 'Live/Available' || displayStatus === 'Live' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                                      displayStatus === 'Reserved' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                                                      'bg-blue-100 text-blue-700 border border-blue-200'
                                                    }`}>
                                                      {(displayStatus === 'Live/Available' || displayStatus === 'Live') && (
                                                        <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                                      )}
                                                      {displayStatus === 'Live/Available' || displayStatus === 'Live' ? 'Live' : displayStatus}
                                                    </span>
                                                    {machine.availableSkus && machine.availableSkus > 0 && (
                                                      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                                        {machine.availableSkus} SKU{machine.availableSkus > 1 ? 's' : ''}
                                                      </span>
                                                    )}
                                                    {machine.requiresTenderProcess && (
                                                      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                                        New Location
                                                      </span>
                                                    )}
                                                  </div>

                                                  <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                                                    <div className="flex items-center gap-3">
                                                      <div onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                          checked={isSelected}
                                                          onCheckedChange={() => handleToggleSelection(machine)}
                                                          className="h-5 w-5"
                                                        />
                                                      </div>
                                                      {machine.MachineMedia && machine.MachineMedia.length > 0 ? (
                                                        <div className="relative h-24 w-32 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                                                          <img
                                                            src={machine.MachineMedia.find(m => m.kind === 'area')?.url || machine.MachineMedia[0].url}
                                                            alt={machine.name}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                                            }}
                                                          />
                                                          <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded">
                                                            {venueLabel}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-lg font-semibold text-blue-700 border border-blue-200">
                                                          {venueLabel}
                                                        </div>
                                                      )}
                                                    </div>
                                                    <div className="flex-1 space-y-2 pr-24 md:pr-32">
                                                      <div>
                                                        {machine.label && (
                                                          <div className="mb-2">
                                                            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                                                              {machine.label}
                                                            </span>
                                                          </div>
                                                        )}
                                                        <h3 className="text-base font-semibold text-slate-900">
                                                          {machine.name}
                                                        </h3>
                                                        {machine.description && (
                                                          <p className="text-sm text-slate-500 line-clamp-1">
                                                            {machine.description}
                                                          </p>
                                                        )}
                                                      </div>
                                                      <div className="grid gap-4 text-sm text-slate-600 sm:grid-cols-3">
                                                        <div>
                                                          <p className="text-xs uppercase text-slate-400">Venue Type</p>
                                                          <p className="font-medium">{venueType}</p>
                                                        </div>
                                                        <div>
                                                          <p className="text-xs uppercase text-slate-400">Location</p>
                                                          <p className="font-medium">{location}</p>
                                                        </div>
                                                        <div>
                                                          <p className="text-xs uppercase text-slate-400">Availability Type</p>
                                                          <p className="font-medium">{availabilityType}</p>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className="flex flex-row items-center gap-2">
                                                    <Button
                                                      variant="outline"
                                                      className="rounded-full px-6 py-2 text-sm font-semibold"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedMachineForDetails(machine);
                                                      }}
                                                    >
                                                      View
                                                    </Button>
                                                    <Button
                                                      variant={isSelected ? 'default' : 'outline'}
                                                      className="rounded-full px-6 py-2 text-sm font-semibold"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleSelection(machine);
                                                      }}
                                                    >
                                                      {isSelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {viewMode === 'map' && (
        <div className="h-[600px] rounded-lg overflow-hidden border border-slate-200 relative">
          <Map
            center={mapCenter}
            zoom={mapZoom}
            markers={mapMarkers}
            mapStyle={mapStyle}
            onBoundsChange={setMapBounds}
          />
          <div className="mt-4 text-sm text-slate-600">
            Showing {visibleMachines.length} machine{visibleMachines.length !== 1 ? 's' : ''} on map
            {selectedMachines.length > 0 && ` • ${selectedMachines.length} selected`}
          </div>
        </div>
      )}

      {/* Machine Details Dialog */}
      <Dialog open={!!selectedMachineForDetails} onOpenChange={(open) => {
        if (!open) {
          setSelectedMachineForDetails(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {selectedMachineForDetails && (() => {
            const machine = selectedMachineForDetails;
            const areaImage = machine.MachineMedia?.find(m => m.kind === 'area');
            const renderImage = machine.MachineMedia?.find(m => m.kind === 'render');
            const hasBothImages = areaImage && renderImage;
            const singleImage = areaImage || renderImage || machine.MachineMedia?.[0];
            const isSelected = selectedMachines.some(m => m.id === machine.id);
            const displayStatus: string = machine.status || 'Live/Available';
            const venueLabel = machine.venueName || machine.venueType || 'Venue';

            return (
              <>
                <DialogHeader className="px-6 pt-6 pb-4 border-b">
                  <DialogTitle className="text-2xl font-semibold">{machine.name}</DialogTitle>
                </DialogHeader>
                
                <div className="overflow-y-auto flex-1 px-6 py-4">
                  {/* Image Section */}
                  <div className="mb-6">
                    {hasBothImages ? (
                      <div className="relative">
                        <BeforeAfterSlider
                          beforeImage={areaImage.url}
                          afterImage={renderImage.url}
                          beforeLabel="Area"
                          afterLabel="Render"
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {venueLabel}
                        </div>
                      </div>
                    ) : singleImage ? (
                      <div className="relative w-full h-96 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={singleImage.url}
                          alt={machine.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute top-4 left-4 bg-blue-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {venueLabel}
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-full h-96 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-2xl font-semibold text-blue-700 mb-2">{venueLabel}</div>
                          <p className="text-sm text-blue-600">No Image Available</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Details Section */}
                  <div className="space-y-6">
                    {/* Header Info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                          displayStatus === 'Live/Available' || displayStatus === 'Live' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                          displayStatus === 'Reserved' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                          'bg-blue-100 text-blue-700 border border-blue-200'
                        }`}>
                          {(displayStatus === 'Live/Available' || displayStatus === 'Live') && (
                            <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                          )}
                          {displayStatus === 'Live/Available' || displayStatus === 'Live' ? 'Live' : displayStatus}
                        </span>
                        {machine.availableSkus && machine.availableSkus > 0 && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            {machine.availableSkus} SKU{machine.availableSkus > 1 ? 's' : ''}
                          </span>
                        )}
                        {machine.requiresTenderProcess && (
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                            New Location
                          </span>
                        )}
                        {machine.label && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
                            {machine.label}
                          </span>
                        )}
                      </div>
                      
                      {machine.description && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {machine.description}
                        </p>
                      )}
                    </div>

                    {/* Technical Details Grid */}
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Status</p>
                        <p className="font-medium text-slate-900">{machine.status || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Available SKUs</p>
                        <p className="font-medium text-slate-900">
                          {machine.availableSkus !== null && machine.availableSkus !== undefined ? machine.availableSkus : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Type</p>
                        <p className="font-medium text-slate-900">{machine.venueType || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Venue Name</p>
                        <p className="font-medium text-slate-900">{machine.venueName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Location</p>
                        <p className="font-medium text-slate-900">{machine.location || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-slate-400 mb-1">Availability Type</p>
                        <p className="font-medium text-slate-900">{machine.availabilityType || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedMachineForDetails(null)}
                  >
                    Close
                  </Button>
                  <Button
                    variant={isSelected ? 'default' : 'outline'}
                    onClick={() => {
                      handleToggleSelection(machine);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isSelected ? 'Selected' : 'Select Machine'}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

